// src/encounter/lib/lab-catalogue.ts
// Pure helpers for the Add lab popup on the lab catalogue v2 (PROD-2640).
//
// Wire shapes mirror Scrips.Master LabCatalogueDTOs.cs (PROD-2637):
//   GET /api/Master/LabCatalogue/panels?status=Published&search=  → { items: LabPanelSummaryDto[], total }
//   GET /api/Master/LabCatalogue/panels/{id}                      → LabPanelDetailDto
// A detail's members are tests or nested panels; nested panels come expanded ONE level
// (member.members), each flagged isPanel. requirement is R (required), O (optional) or Rflx (reflex:
// the lab adds it only when a rule fires).
//
// The popup's rules, all here so they are testable without rendering:
//   - members are grouped by nested panel (direct tests in an untitled group, in display order);
//   - R and O tests are selectable and selected by default; Rflx tests are shown greyed, never
//     selectable, and never sent (not in the selected list, not in the omitted list);
//   - a test is listed once (the first place it appears), matching the legacy read's flattening;
//   - CPT panels and lab panels are LOCKED (ordered as one panel by the lab); house panels are
//     tickable, and a test left out bills the panel as its components;
//   - "Already ordered in <panel>; drawn once. Not added again." when another lab on the plan
//     carries the same code (a nested panel collapses to ONE such row). Such a member goes out in
//     `alreadyOrderedTests` (Patient #1001, P-B7) with its catalogue code/system verbatim, and in
//     neither the selected nor the omitted list.
// Design: canvas "Lab Panels and Tests", artboards 6-8 (AddLabAtomic / AddLabHouse / AddLabDuplicate).
import type { PlanOrder } from '@scripsteam/scrips-signal-ds';
import type { OrderCoding, OrderPayloadMap } from './order-payload';

export type LabPanelKind = 'CptPanel' | 'LabPanel' | 'HousePanel';

export interface LabCatalogueMemberDto {
  id: string;
  memberTestId?: string | null;
  memberPanelId?: string | null;
  isPanel?: boolean;
  requirement?: string | null;
  reflexRule?: string | null;
  displayOrder?: number;
  name?: string | null;
  code?: string | null;
  system?: string | null;
  loinc?: string | null;
  cptCode?: string | null;
  specimenDefinitionId?: string | null;
  specimenName?: string | null;
  labVendorId?: string | null;
  panelKind?: string | null;
  panelVersion?: number | null;
  panelStatus?: string | null;
  members?: LabCatalogueMemberDto[] | null;
}

export interface LabCataloguePanelSummaryDto {
  id: string;
  organizationId?: string | null;
  isGlobal?: boolean;
  kind?: string | null;
  code?: string | null;
  system?: string | null;
  name?: string | null;
  cptPanelCode?: string | null;
  /**
   * The panel's own LOINC. NOT on Master's DTO yet (P-B1): the slot is read here so the code line
   * shows "LOINC <x>" the moment the backend sends it, with nothing else to change.
   */
  loinc?: string | null;
  version: number;
  status?: string | null;
  labVendorId?: string | null;
}

export interface LabCataloguePanelDto extends LabCataloguePanelSummaryDto {
  members?: LabCatalogueMemberDto[] | null;
}

export interface LabCataloguePanelListDto {
  items?: LabCataloguePanelSummaryDto[] | null;
  total?: number;
}

/** One test row in the popup. */
export interface LabCatalogueRow {
  /** Selection key — the same keyOf rule the legacy chips use (code, else display). */
  key: string;
  coding: OrderCoding;
  loinc?: string;
  /** Rflx: shown greyed, never selectable, never sent. */
  reflex: boolean;
  reflexRule?: string;
  specimenName?: string;
  /** The member's CPT, for the house panel's "Bills as components" box. */
  cptCode?: string;
}

/** Tests grouped by nested panel. `title` null = the panel's own direct tests. */
export interface LabCatalogueGroup {
  key: string;
  title: string | null;
  /** A nested panel's own code and LOINC (absent on the direct-tests group). */
  code?: string;
  loinc?: string;
  /**
   * A nested panel's own coding, Master's code/system VERBATIM (like LabCatalogueRow.coding), for
   * the `alreadyOrderedTests` entry when the whole nested panel is already on the plan.
   */
  coding?: OrderCoding;
  rows: LabCatalogueRow[];
}

/** Another lab already on this plan — for the "Already ordered in" hint. */
export interface OtherLabOnPlan {
  name: string;
  /** The lab's own code plus its selected members' codes. */
  codes: string[];
}

const same = (a?: string | null, b?: string | null) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

const isReflex = (requirement?: string | null) => same(requirement, 'Rflx');

export function kindLabel(kind?: string | null): string {
  if (same(kind, 'CptPanel')) return 'CPT panel';
  if (same(kind, 'LabPanel')) return 'Lab panel';
  if (same(kind, 'HousePanel')) return 'House panel';
  return 'Lab panel';
}

/**
 * CPT panels and lab panels are ordered as ONE panel by the lab: their tests are locked (6.10). A
 * house panel is the clinic's own grouping, so each test can be left out (7.6). An unknown kind is
 * treated as a lab panel, matching kindLabel.
 */
export function isLockedKind(kind?: string | null): boolean {
  return !same(kind, 'HousePanel');
}

/** The header's kind chip colour: blue for CPT and lab panels, grey for house panels (6.3). */
export function kindTone(kind?: string | null): 'blue' | 'grey' {
  return same(kind, 'HousePanel') ? 'grey' : 'blue';
}

/**
 * The code line under the panel name (6.4 / 7.2):
 *   CPT panel   "19040 · LOINC 24362-6 · CPT 80069"   (LOINC only once Master sends it, P-B1)
 *   Lab panel   "19040 · LOINC 24362-6"
 *   House panel "9024010 · house panel · no panel LOINC or CPT"
 */
export function panelCodeLine(
  panel: Pick<LabCataloguePanelSummaryDto, 'kind' | 'code' | 'loinc' | 'cptPanelCode'>,
): string {
  const code = (panel.code ?? '').trim();
  if (same(panel.kind, 'HousePanel')) {
    return [code, 'house panel', 'no panel LOINC or CPT'].filter(Boolean).join(' · ');
  }
  const loinc = panel.loinc?.trim();
  const cpt = same(panel.kind, 'CptPanel') ? panel.cptPanelCode?.trim() : undefined;
  return [code, loinc ? `LOINC ${loinc}` : '', cpt ? `CPT ${cpt}` : ''].filter(Boolean).join(' · ');
}

/** The blue chip for a CPT panel (6.7): "Bills as 80069". Null for any other kind. */
export function billsAsChip(panel: Pick<LabCataloguePanelSummaryDto, 'kind' | 'cptPanelCode'>): string | null {
  const cpt = panel.cptPanelCode?.trim();
  return same(panel.kind, 'CptPanel') && cpt ? `Bills as ${cpt}` : null;
}

/**
 * The published panel for a picked lab code/system, or null. The code must match exactly; the system
 * too when both sides carry one. The caller's own panel wins over a global one, then the newest version.
 */
export function pickPublishedPanel(
  items: readonly LabCataloguePanelSummaryDto[] | null | undefined,
  code: string,
  system?: string | null,
): LabCataloguePanelSummaryDto | null {
  const hits = (items ?? []).filter(
    (p) =>
      same(p.status, 'Published') &&
      same(p.code, code) &&
      (!system?.trim() || !p.system?.trim() || same(p.system, system)),
  );
  hits.sort((a, b) => Number(!!a.isGlobal) - Number(!!b.isGlobal) || b.version - a.version);
  return hits[0] ?? null;
}

/**
 * A member → its row. The coding's `code` and `system` are Master's catalogue member DTO values
 * VERBATIM (no trim, no case change): at sign, Scrips.Patient (#995) rebuilds the lab's tests from
 * the catalogue version keyed on code + system, and drops an omitted member only on an EXACT match.
 * A respelled code or system would get a deselected test ordered anyway.
 */
function toRow(m: LabCatalogueMemberDto, reflex: boolean): LabCatalogueRow | null {
  const name = (m.name ?? '').trim();
  const code = m.code ?? '';
  if (!name && !code.trim()) return null;
  const coding: OrderCoding = {
    code: code.trim() ? code : name,
    displayName: name || code,
    ...(m.system != null && m.system !== '' ? { system: m.system } : {}),
  };
  return {
    key: coding.code || coding.displayName,
    coding,
    ...(m.loinc?.trim() ? { loinc: m.loinc.trim() } : {}),
    reflex,
    ...(m.reflexRule?.trim() ? { reflexRule: m.reflexRule.trim() } : {}),
    ...(m.specimenName?.trim() ? { specimenName: m.specimenName.trim() } : {}),
    ...(m.cptCode?.trim() ? { cptCode: m.cptCode.trim() } : {}),
  };
}

const byOrder = (a: LabCatalogueMemberDto, b: LabCatalogueMemberDto) =>
  (a.displayOrder ?? 0) - (b.displayOrder ?? 0);

/**
 * The panel's tests grouped by nested panel, in display order. Consecutive direct tests share an
 * untitled group; each nested panel is its own titled group. A nested panel marked Rflx makes all of
 * its tests reflex. A second-level panel (not expanded by the API) is listed as one row by its code.
 */
export function groupPanelMembers(panel: LabCataloguePanelDto): LabCatalogueGroup[] {
  const seen = new Set<string>();
  const groups: LabCatalogueGroup[] = [];
  let direct: LabCatalogueGroup | null = null;

  const add = (group: LabCatalogueGroup, m: LabCatalogueMemberDto, reflex: boolean) => {
    const row = toRow(m, reflex);
    if (!row || seen.has(row.key)) return;
    seen.add(row.key);
    group.rows.push(row);
  };

  for (const m of [...(panel.members ?? [])].sort(byOrder)) {
    const nested = m.isPanel || !!m.memberPanelId;
    if (nested && m.members?.length) {
      direct = null;
      const group: LabCatalogueGroup = {
        key: m.id,
        title: (m.name ?? m.code ?? '').trim() || null,
        ...(m.code?.trim() ? { code: m.code.trim() } : {}),
        ...(m.loinc?.trim() ? { loinc: m.loinc.trim() } : {}),
        ...(m.code?.trim()
          ? {
              coding: {
                code: m.code,
                displayName: (m.name ?? '').trim() || m.code,
                ...(m.system != null && m.system !== '' ? { system: m.system } : {}),
              },
            }
          : {}),
        rows: [],
      };
      for (const child of [...m.members].sort(byOrder)) {
        add(group, child, isReflex(m.requirement) || isReflex(child.requirement));
      }
      if (group.rows.length) groups.push(group);
    } else {
      if (!direct) {
        direct = { key: `direct-${m.id}`, title: null, rows: [] };
        groups.push(direct);
      }
      add(direct, m, isReflex(m.requirement));
    }
  }
  return groups.filter((g) => g.rows.length > 0);
}

export function allRows(groups: readonly LabCatalogueGroup[]): LabCatalogueRow[] {
  return groups.flatMap((g) => g.rows);
}

/** The rows the clinician can select (R and O). */
export function selectableRows(groups: readonly LabCatalogueGroup[]): LabCatalogueRow[] {
  return allRows(groups).filter((r) => !r.reflex);
}

/** Distinct specimen names across the panel's tests, in order. */
export function specimensOf(groups: readonly LabCatalogueGroup[]): string[] {
  const out: string[] = [];
  for (const r of allRows(groups)) {
    if (r.specimenName && !out.some((s) => same(s, r.specimenName))) out.push(r.specimenName);
  }
  return out;
}

export function duplicateHint(otherName: string): string {
  return `Already ordered in ${otherName}; drawn once. Not added again.`;
}

/**
 * The reflex row's note (8.5): "Added by the lab if <rule>. Billed only then." Master's rule text
 * may or may not start with "if", and may end with a full stop; both are normalised.
 */
export function reflexNote(rule?: string | null): string {
  const r = (rule ?? '').trim().replace(/^if\s+/i, '').replace(/[.\s]+$/, '');
  // The no-rule string is copy unreviewed: no design artboard covers a reflex test without a rule.
  return r ? `Added by the lab if ${r}. Billed only then.` : 'Added by the lab. Billed only then.';
}

/** One row of the Tests list, as the popup draws it. */
export type LabTestViewState = 'test' | 'reflex' | 'already';

export interface LabTestView {
  /** The selection key for a 'test' row; the group or row key otherwise. */
  key: string;
  name: string;
  /** Absent → the row shows "—". */
  loinc?: string;
  state: LabTestViewState;
  /** The reflex or already-ordered note. */
  note?: string;
}

export interface LabTestViews {
  views: LabTestView[];
  /**
   * Selection keys that sit under an "already ordered" row. They are NOT sent as selected or
   * omitted tests (Patient #1001, P-B7): the order carries `alreadyOrdered` instead.
   */
  alreadyKeys: Set<string>;
  /**
   * The wire `alreadyOrderedTests` entries, one per "already ordered" row, in list order: the
   * member's catalogue coding VERBATIM (a nested panel is ONE entry, its own coding, and covers all
   * of its tests). Patient matches the owner on code + system, so a respelling orders it twice.
   */
  alreadyOrdered: OrderCoding[];
}

/**
 * The popup's rows, in order (6.9 / 8.4 / 8.5). No nested-panel titles: a nested panel's tests are
 * listed in place. A nested panel that another lab on the plan already is (matched by its code)
 * collapses to ONE "already ordered" row; a single test another lab already contains is one such
 * row too. Reflex tests are their own rows, carrying the lab's rule.
 */
export function toTestViews(
  groups: readonly LabCatalogueGroup[],
  others: readonly OtherLabOnPlan[] | undefined,
): LabTestViews {
  const views: LabTestView[] = [];
  const alreadyKeys = new Set<string>();
  const alreadyOrdered: OrderCoding[] = [];
  const holder = (code?: string) =>
    code ? (others ?? []).find((o) => o.codes.some((c) => same(c, code))) : undefined;
  for (const g of groups) {
    const panelHolder = holder(g.code);
    if (panelHolder && g.coding) {
      for (const r of g.rows) if (!r.reflex) alreadyKeys.add(r.key);
      alreadyOrdered.push(g.coding);
      views.push({
        key: g.key,
        name: g.title ?? g.code ?? '',
        ...(g.loinc ? { loinc: g.loinc } : {}),
        state: 'already',
        note: duplicateHint(panelHolder.name),
      });
      continue;
    }
    for (const r of g.rows) {
      const base = { key: r.key, name: r.coding.displayName, ...(r.loinc ? { loinc: r.loinc } : {}) };
      if (r.reflex) {
        views.push({ ...base, state: 'reflex', note: reflexNote(r.reflexRule) });
        continue;
      }
      const other = holder(r.coding.code);
      if (other) {
        alreadyKeys.add(r.key);
        alreadyOrdered.push(r.coding);
        views.push({ ...base, state: 'already', note: duplicateHint(other.name) });
      } else {
        views.push({ ...base, state: 'test' });
      }
    }
  }
  return { views, alreadyKeys, alreadyOrdered };
}

/**
 * Reopen (edit) seed: the selection keys a saved `alreadyOrderedTests` list covers. A test entry
 * covers its own row; a nested-panel entry covers every non-reflex test under it. Matched on code
 * (and system when both carry one), trimmed and case-insensitive, like Patient's lab keys. The
 * saved snapshot leaves these tests out, so without this seed they would reopen as left out; if
 * their owner is no longer on the plan, they are then ordered here, as Patient's fallback does.
 */
export function keysCoveredByAlreadyOrdered(
  groups: readonly LabCatalogueGroup[],
  entries: readonly OrderCoding[] | undefined,
): string[] {
  if (!entries?.length) return [];
  const hit = (c: OrderCoding | undefined) =>
    !!c &&
    entries.some(
      (e) => same(e.code, c.code) && (!e.system?.trim() || !c.system?.trim() || same(e.system, c.system)),
    );
  const out: string[] = [];
  for (const g of groups) {
    const whole = hit(g.coding);
    for (const r of g.rows) if (!r.reflex && (whole || hit(r.coding))) out.push(r.key);
  }
  return out;
}

/**
 * The Tests header (6.9 / 8.3): "Tests (10/10)", or, when the list has already-ordered or reflex
 * rows, "Tests (3 new · 1 already ordered · 1 reflex)" (a zero part is left out).
 */
export function testsHeader(counts: {
  selected: number;
  selectable: number;
  newSelected: number;
  already: number;
  reflex: number;
}): string {
  if (!counts.already && !counts.reflex) return `Tests (${counts.selected}/${counts.selectable})`;
  const parts = [
    `${counts.newSelected} new`,
    counts.already ? `${counts.already} already ordered` : '',
    counts.reflex ? `${counts.reflex} reflex` : '',
  ].filter(Boolean);
  return `Tests (${parts.join(' · ')})`;
}

/** The lock hint for CPT and lab panels (6.11). */
export const LOCKED_HINT =
  'Ordered as one panel by the lab. To leave a test out, order the tests you need one by one.';
/** The house panel hint (7.7). */
export const HOUSE_HINT = 'House panel: untick a test to leave it out.';
/** The footer hint when the list has already-ordered rows (8.6). */
export const SHARED_HINT = 'Shared tests are drawn and billed once for this visit.';
/** A left-out house-panel row (7.6). */
export const LEFT_OUT_NOTE = 'Left out. A package entitlement is still used for this panel.';

/**
 * The house panel's amber box when a test is left out (7.8): the CPTs of the tests still ordered,
 * then "n test(s) left out; the lab is told it was omitted on purpose." Null when nothing is left out.
 */
export function billsAsComponents(
  ordered: readonly Pick<LabCatalogueRow, 'cptCode'>[],
  leftOut: number,
): { cpts: string[]; text: string } | null {
  if (leftOut <= 0) return null;
  const cpts: string[] = [];
  for (const r of ordered) if (r.cptCode && !cpts.includes(r.cptCode)) cpts.push(r.cptCode);
  return {
    cpts,
    text: `${leftOut} ${leftOut === 1 ? 'test' : 'tests'} left out; the lab is told it was omitted on purpose.`,
  };
}

/**
 * The labs already on this plan (their own code + selected members) for the duplicate hint. The
 * order being edited is excluded BY ID, so an edited lab never flags its own tests as duplicates.
 */
export function otherLabsOnPlan(
  orders: readonly PlanOrder[],
  payloads: OrderPayloadMap | undefined,
  excludeOrderId?: string,
): OtherLabOnPlan[] {
  const out: OtherLabOnPlan[] = [];
  for (const o of orders) {
    if (o.type !== 'lab' || o.id === excludeOrderId) continue;
    const payload = payloads?.get(o.id);
    if (payload?.kind !== 'lab') continue;
    out.push({
      name: payload.code.displayName,
      codes: [payload.code.code, ...payload.subTests.map((t) => t.code)].filter(Boolean),
    });
  }
  return out;
}
